// ============================================
// NAVBAR CLOCK + LANGUAGE SELECTOR
// ============================================

import Alpine from 'alpinejs';

interface LangItem {
    code: string;
    flag: string;
    native: string;
    url: string;
    active: boolean;
}

interface CurrencyItem {
    code: string;
    symbol: string;
    flag: string;
    name: string;
    active: boolean;
}

export function registerNavbarClock(): void {
    Alpine.data('navbarClock', () => ({
        time: '--:--:-- --',
        date: '------',
        langOpen: false,
        currencyOpen: false,
        hoverTimeout: null as ReturnType<typeof setTimeout> | null,
        currencyHoverTimeout: null as ReturnType<typeof setTimeout> | null,
        intervalId: null as ReturnType<typeof setInterval> | null,

        // Injected from PHP via x-init
        languages: [] as LangItem[],
        currentLang: 'en',
        useCurrencySwitch: false,
        currencies: [] as CurrencyItem[],
        currentCurrency: 'USD',

        init() {
            this.updateTime();
            this.intervalId = setInterval(() => this.updateTime(), 1000);

            // Sync currency from localStorage if available
            if (this.useCurrencySwitch) {
                const stored = localStorage.getItem('store_selected_currency');
                if (stored) this.currentCurrency = stored;
            }
        },

        updateTime() {
            const now = new Date();
            const locale = this.currentLang || 'en';

            this.time = new Intl.DateTimeFormat(locale, {
                timeZone: 'America/Los_Angeles',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: true,
            }).format(now);

            this.date = new Intl.DateTimeFormat(locale, {
                timeZone: 'America/Los_Angeles',
                weekday: 'short',
                month: 'short',
                day: 'numeric',
            }).format(now);

        },

        // Desktop: hover open/close with delay to prevent flickering
        isDesktop(): boolean {
            return window.innerWidth >= 1024;
        },

        onMouseEnter() {
            if (!this.isDesktop()) return;
            if (this.hoverTimeout) clearTimeout(this.hoverTimeout);
            this.langOpen = true;
        },

        onMouseLeave() {
            if (!this.isDesktop()) return;
            this.hoverTimeout = setTimeout(() => { this.langOpen = false; }, 150);
        },

        // Mobile: click toggle
        toggleLang() {
            if (this.isDesktop()) return;
            this.langOpen = !this.langOpen;
        },

        switchLang(lang: string, url: string) {
            const year = 365 * 86400;
            document.cookie = `preferred_language=${lang}; path=/; max-age=${year}; SameSite=Strict; Secure`;
            document.cookie = `manual_language_switch=true; path=/; max-age=10; SameSite=Strict; Secure`;
            document.cookie = `has_visited=true; path=/; max-age=${year}; SameSite=Strict; Secure`;
            sessionStorage.setItem('ulcs_scroll_pos', String(window.scrollY));
            sessionStorage.setItem('ulcs_scroll_time', String(Date.now()));
            //window.location.href = url;
            window.location.href = '/?switch_lang=' + lang + '&target_url=' + encodeURIComponent(url);
        },

        // Currency dropdown: hover on desktop, click on mobile
        onCurrencyMouseEnter() {
            if (!this.isDesktop()) return;
            if (this.currencyHoverTimeout) clearTimeout(this.currencyHoverTimeout);
            this.currencyOpen = true;
        },

        onCurrencyMouseLeave() {
            if (!this.isDesktop()) return;
            this.currencyHoverTimeout = setTimeout(() => { this.currencyOpen = false; }, 150);
        },

        toggleCurrency() {
            if (this.isDesktop()) return;
            this.currencyOpen = !this.currencyOpen;
        },

        switchCurrency(code: string) {
            this.currentCurrency = code;
            this.currencyOpen = false;
            localStorage.setItem('store_selected_currency', code);
            document.cookie = `store_selected_currency=${code}; path=/; max-age=${365 * 86400}; SameSite=Strict; Secure`;
            sessionStorage.setItem('ulcs_scroll_pos', String(window.scrollY));
            sessionStorage.setItem('ulcs_scroll_time', String(Date.now()));
            window.location.reload();
        },

        get currentLangData(): LangItem | undefined {
            return this.languages.find((l: LangItem) => l.active) || this.languages[0];
        },

        get currentCurrencyData(): CurrencyItem | undefined {
            return this.currencies.find((c: CurrencyItem) => c.code === this.currentCurrency) || this.currencies[0];
        },

        destroy() {
            if (this.intervalId) clearInterval(this.intervalId);
            if (this.hoverTimeout) clearTimeout(this.hoverTimeout);
        }
    }));
}
